import React, { useState, useEffect } from 'react';
import {
  Box,
  Container,
  Typography,
  Paper,
  Divider,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Fade,
  useTheme,
  useMediaQuery
} from '@mui/material';
import {
  Security as SecurityIcon,
  DataUsage as DataUsageIcon,
  Cookie as CookieIcon,
  Visibility as VisibilityIcon,
  Lock as LockIcon,
  Shield as ShieldIcon
} from '@mui/icons-material';
import { publicService } from '../../services/api';

const Privacy = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [settings, setSettings] = useState({
    business: {
      name: '',
      email: '',
      phone: ''
    }
  });

  useEffect(() => {
    const fetchSettings = async () => {
      try {
        const response = await publicService.getSettings();
        const data = response.data.data;
        setSettings({
          business: {
            name: data.business_name || 'ASO',
            email: data.business_email || '',
            phone: data.business_phone || ''
          }
        });
      } catch (error) {
        console.error('Ayarlar yüklenirken hata oluştu:', error);
      }
    };

    fetchSettings();
  }, []);

  return (
    <Box sx={{ py: { xs: 4, md: 8 } }}>
      <Container maxWidth="lg">
        <Fade in timeout={800}>
          <Box>
            {/* Header */}
            <Box sx={{ textAlign: 'center', mb: 6 }}>
              <Typography
                variant="h3"
                sx={{
                  fontWeight: 700,
                  color: 'primary.main',
                  mb: 2,
                  fontSize: { xs: '2rem', md: '3rem' }
                }}
              >
                Gizlilik Politikası
              </Typography>
              <Typography
                variant="h6"
                color="text.secondary"
                sx={{ maxWidth: 600, mx: 'auto' }}
              >
                Kişisel verilerinizin güvenliği bizim için çok önemlidir
              </Typography>
            </Box>

            <Paper
              elevation={3}
              sx={{
                p: { xs: 3, md: 6 },
                borderRadius: 3,
                background: 'linear-gradient(135deg, #fafdff 0%, #e3f0fc 100%)'
              }}
            >
              {/* Son Güncelleme */}
              <Box sx={{ mb: 4, p: 3, bgcolor: 'primary.50', borderRadius: 2 }}>
                <Typography variant="h6" color="primary.main" sx={{ fontWeight: 600, mb: 1 }}>
                  Son Güncelleme: 08.08.2025
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Bu gizlilik politikası, {settings.business.name} tarafından yönetilmektedir.
                </Typography>
              </Box>

              {/* Bölümler */}
              <Box sx={{ mb: 4 }}>
                <Typography variant="h5" sx={{ fontWeight: 600, mb: 3, color: 'primary.dark' }}>
                  <SecurityIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
                  1. Toplanan Bilgiler
                </Typography>
                <Typography variant="body1" sx={{ mb: 2, lineHeight: 1.8 }}>
                  Hizmetlerimizi sunarken aşağıdaki bilgileri topluyoruz:
                </Typography>
                <List>
                  <ListItem>
                    <ListItemIcon>
                      <DataUsageIcon color="primary" />
                    </ListItemIcon>
                    <ListItemText
                      primary="Kişisel Bilgiler"
                      secondary="Ad, soyad, telefon numarası, e-posta adresi, araç bilgileri"
                    />
                  </ListItem>
                  <ListItem>
                    <ListItemIcon>
                      <VisibilityIcon color="primary" />
                    </ListItemIcon>
                    <ListItemText
                      primary="Randevu Bilgileri"
                      secondary="Randevu tarihi, saat, hizmet türü, araç modeli ve plakası"
                    />
                  </ListItem>
                  <ListItem>
                    <ListItemIcon>
                      <CookieIcon color="primary" />
                    </ListItemIcon>
                    <ListItemText
                      primary="Teknik Bilgiler"
                      secondary="IP adresi, tarayıcı bilgileri, çerezler ve kullanım istatistikleri"
                    />
                  </ListItem>
                </List>
              </Box>

              <Divider sx={{ my: 4 }} />

              <Box sx={{ mb: 4 }}>
                <Typography variant="h5" sx={{ fontWeight: 600, mb: 3, color: 'primary.dark' }}>
                  <LockIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
                  2. Bilgilerin Kullanımı
                </Typography>
                <Typography variant="body1" sx={{ mb: 2, lineHeight: 1.8 }}>
                  Topladığımız bilgileri aşağıdaki amaçlarla kullanıyoruz:
                </Typography>
                <List>
                  <ListItem>
                    <ListItemText
                      primary="Hizmet Sunumu"
                      secondary="Randevu oluşturma, servis hizmetleri, iletişim kurma"
                    />
                  </ListItem>
                  <ListItem>
                    <ListItemText
                      primary="İyileştirme"
                      secondary="Hizmet kalitesini artırma, müşteri deneyimini geliştirme"
                    />
                  </ListItem>
                  <ListItem>
                    <ListItemText
                      primary="Yasal Yükümlülükler"
                      secondary="Yasal gereklilikleri yerine getirme, güvenlik sağlama"
                    />
                  </ListItem>
                </List>
              </Box>

              <Divider sx={{ my: 4 }} />

              <Box sx={{ mb: 4 }}>
                <Typography variant="h5" sx={{ fontWeight: 600, mb: 3, color: 'primary.dark' }}>
                  <ShieldIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
                  3. Bilgi Güvenliği
                </Typography>
                <Typography variant="body1" sx={{ mb: 2, lineHeight: 1.8 }}>
                  Kişisel verilerinizin güvenliği için aşağıdaki önlemleri alıyoruz:
                </Typography>
                <List>
                  <ListItem>
                    <ListItemText
                      primary="Şifreleme"
                      secondary="Tüm veri iletişimi SSL/TLS şifreleme ile korunmaktadır"
                    />
                  </ListItem>
                  <ListItem>
                    <ListItemText
                      primary="Erişim Kontrolü"
                      secondary="Sadece yetkili personel kişisel verilere erişebilir"
                    />
                  </ListItem>
                  <ListItem>
                    <ListItemText
                      primary="Düzenli Güvenlik Denetimleri"
                      secondary="Sistemlerimiz düzenli olarak güvenlik testlerinden geçer"
                    />
                  </ListItem>
                </List>
              </Box>

              <Divider sx={{ my: 4 }} />

              <Box sx={{ mb: 4 }}>
                <Typography variant="h5" sx={{ fontWeight: 600, mb: 3, color: 'primary.dark' }}>
                  4. Bilgi Paylaşımı
                </Typography>
                <Typography variant="body1" sx={{ mb: 2, lineHeight: 1.8 }}>
                  Kişisel verilerinizi üçüncü taraflarla paylaşmıyoruz, ancak aşağıdaki durumlar hariç:
                </Typography>
                <List>
                  <ListItem>
                    <ListItemText
                      primary="Yasal Zorunluluk"
                      secondary="Yasal gereklilikler durumunda yetkili makamlarla paylaşım"
                    />
                  </ListItem>
                  <ListItem>
                    <ListItemText
                      primary="Hizmet Sağlayıcıları"
                      secondary="Sadece hizmet sunumu için gerekli olan güvenilir ortaklarla"
                    />
                  </ListItem>
                </List>
              </Box>

              <Divider sx={{ my: 4 }} />

              <Box sx={{ mb: 4 }}>
                <Typography variant="h5" sx={{ fontWeight: 600, mb: 3, color: 'primary.dark' }}>
                  5. Çerezler (Cookies)
                </Typography>
                <Typography variant="body1" sx={{ mb: 2, lineHeight: 1.8 }}>
                  Web sitemizde kullanıcı deneyimini iyileştirmek için çerezler kullanıyoruz:
                </Typography>
                <List>
                  <ListItem>
                    <ListItemText
                      primary="Gerekli Çerezler"
                      secondary="Sitenin temel işlevleri için gerekli olan çerezler"
                    />
                  </ListItem>
                  <ListItem>
                    <ListItemText
                      primary="Analitik Çerezler"
                      secondary="Site kullanımını analiz etmek için kullanılan çerezler"
                    />
                  </ListItem>
                </List>
              </Box>

              <Divider sx={{ my: 4 }} />

              <Box sx={{ mb: 4 }}>
                <Typography variant="h5" sx={{ fontWeight: 600, mb: 3, color: 'primary.dark' }}>
                  6. Haklarınız
                </Typography>
                <Typography variant="body1" sx={{ mb: 2, lineHeight: 1.8 }}>
                  KVKK kapsamında aşağıdaki haklara sahipsiniz:
                </Typography>
                <List>
                  <ListItem>
                    <ListItemText
                      primary="Bilgi Alma Hakkı"
                      secondary="Hakkınızda hangi bilgilerin işlendiğini öğrenme"
                    />
                  </ListItem>
                  <ListItem>
                    <ListItemText
                      primary="Düzeltme Hakkı"
                      secondary="Yanlış veya eksik bilgilerin düzeltilmesini isteme"
                    />
                  </ListItem>
                  <ListItem>
                    <ListItemText
                      primary="Silme Hakkı"
                      secondary="Kişisel verilerinizin silinmesini isteme"
                    />
                  </ListItem>
                  <ListItem>
                    <ListItemText
                      primary="İşlemeyi Sınırlama Hakkı"
                      secondary="Veri işlemenin sınırlandırılmasını isteme"
                    />
                  </ListItem>
                </List>
              </Box>

              <Divider sx={{ my: 4 }} />

              <Box sx={{ mb: 4 }}>
                <Typography variant="h5" sx={{ fontWeight: 600, mb: 3, color: 'primary.dark' }}>
                  7. İletişim
                </Typography>
                <Typography variant="body1" sx={{ mb: 2, lineHeight: 1.8 }}>
                  Gizlilik politikamız hakkında sorularınız için bizimle iletişime geçebilirsiniz:
                </Typography>
                <Box sx={{ p: 3, bgcolor: 'grey.50', borderRadius: 2 }}>
                  <Typography variant="body1" sx={{ mb: 1 }}>
                    <strong>E-posta:</strong> {settings.business.email || 'info@aso.com'}
                  </Typography>
                  <Typography variant="body1" sx={{ mb: 1 }}>
                    <strong>Telefon:</strong> {settings.business.phone || '+90 xxx xxx xx xx'}
                  </Typography>
                  <Typography variant="body1">
                    <strong>Adres:</strong> {settings.business.name} - Türkiye
                  </Typography>
                </Box>
              </Box>
            </Paper>
          </Box>
        </Fade>
      </Container>
    </Box>
  );
};

export default Privacy; 